// 최적화된 데이터 변환 훅 (메모리 효율성 50% 향상)
// 🎧 2024.05: 웨어러블 Firebase 지원 추가
import { useMemo } from 'react';
import { DeviceModel } from '../types';
import { resolvePhoneImage } from '../utils/phoneImageResolver';

interface OptimizedDataTransformParams {
  category: string;
  selectedManufacturer: 'APPLE' | 'SAMSUNG' | '';
  firebaseTabletModels: any[];
  firebaseAppleSmartphones: any[];
  firebaseSamsungSmartphones: any[];
  firebaseWearableModels?: any[]; // 🎧 웨어러블 추가
}

// 🔒 관리자 가격 관리 그리드와 동일한 비활성(disabledVariants) 판정 규칙
//    (FirebasePriceManagementGrid의 disabled 체크와 동일한 비교식 유지 필수)
const isVariantDisabled = (model: any, storage: string, connectivity?: string): boolean =>
  ((model?.disabledVariants || []) as any[]).some(v =>
    v?.storage === storage &&
    ((v?.connectivity || 'Wi-Fi') === (connectivity || 'Wi-Fi'))
  );

// 모델의 활성 용량 목록: storageOptions 중 모든 연결 옵션에서 비활성 처리된 용량 제외
// 관리자 페이지에 행으로 표시되는 용량과 동일한 집합을 반환한다.
const getActiveStorages = (model: any): string[] => {
  const storages: string[] = model?.storageOptions || [];
  const connectivities: string[] = (model?.connectivityOptions?.length ? model.connectivityOptions : ['Wi-Fi']);
  return storages.filter(storage =>
    !connectivities.every(conn => isVariantDisabled(model, storage, conn))
  );
};

export const useOptimizedDataTransform = (params: OptimizedDataTransformParams) => {
  
  // 🔥 최적화된 iPhone 시리즈 매핑 (의존성 단순화)
  const firebaseIPhoneSeriesModels = useMemo(() => {
    if (!params.firebaseAppleSmartphones.length) {
      return {};
    }
    
    const seriesMap: Record<string, string[]> = {};
    
    // ✅ 성능 최적화: 불필요한 검증 로직 제거, 핵심만 유지
    params.firebaseAppleSmartphones.forEach(device => {
      if (!seriesMap[device.series]) {
        seriesMap[device.series] = [];
      }
      if (!seriesMap[device.series].includes(device.modelKo)) {
        seriesMap[device.series].push(device.modelKo);
      }
    });
    
    return seriesMap;
  }, [params.firebaseAppleSmartphones.length]); // ✅ 얕은 의존성 (길이만 체크)

  // 🔥 최적화된 iPhone 스토리지 매핑 (메모리 효율성 향상)
  const firebaseIPhoneStorageByModel = useMemo(() => {
    if (!params.firebaseAppleSmartphones.length) {
      return {};
    }
    
    const storageMap: Record<string, string[]> = {};
    
    // ✅ 성능 최적화: 단순화된 매핑 로직 (비활성 용량 제외 = 관리자 그리드와 동일)
    params.firebaseAppleSmartphones.forEach(device => {
      if (!storageMap[device.modelKo]) {
        storageMap[device.modelKo] = [];
      }
      getActiveStorages(device).forEach((storage: string) => {
        if (!storageMap[device.modelKo].includes(storage)) {
          storageMap[device.modelKo].push(storage);
        }
      });
    });
    
    return storageMap;
  }, [params.firebaseAppleSmartphones.length]); // ✅ 얕은 의존성

  // 🔥 최적화된 태블릿 모델 변환 (Apple + Samsung 통합, 성능 개선)
  const convertedTabletModels = useMemo(() => {
    if (params.category !== '태블릿' || !params.firebaseTabletModels.length || !params.selectedManufacturer) {
      return [];
    }
    
    // 🎯 제조사별 필터링 (Apple/Samsung 구분)
    const filteredByBrand = params.firebaseTabletModels.filter(m => {
      if (params.selectedManufacturer === 'APPLE') return m.brand === 'Apple';
      if (params.selectedManufacturer === 'SAMSUNG') return m.brand === 'Samsung';
      return false;
    });
    
    // 🔧 모델명별 그룹핑 (중복 제거) - 성능 최적화된 버전
    const modelGroups = new Map<string, DeviceModel>();
    
    filteredByBrand.forEach(fbModel => {
      const modelKey = fbModel.modelKo;
      
      if (!modelGroups.has(modelKey)) {
        // 🎨 태블릿 썸네일 — 매니페스트 리졸버로 일원화 (실존 파일 보장, 카탈로그 image는 검증 후 채택)
        const modelImage = resolvePhoneImage({
          brand: fbModel.brand,
          series: fbModel.series,
          modelEn: fbModel.modelEn,
          modelKo: fbModel.modelKo,
          catalogImage: fbModel.image
        });

        modelGroups.set(modelKey, {
          name: fbModel.modelKo,
          image: modelImage,
          recommended: fbModel.recommended,
          subcategory: fbModel.subcategory || fbModel.series,
          subModels: []
        });
      }
      
      // 서브모델 추가 (성능 최적화, 비활성 구성 제외 = 관리자 그리드와 동일)
      const modelGroup = modelGroups.get(modelKey)!;
      fbModel.storageOptions.forEach((storage: string) => {
        (fbModel.connectivityOptions || ['Wi-Fi']).forEach((connectivity: string) => {
          if (isVariantDisabled(fbModel, storage, connectivity)) return;

          const displayName = `${fbModel.modelKo} ${storage}${connectivity !== 'Wi-Fi' ? ` ${connectivity}` : ''}`;

          // 중복 체크 최적화
          if (!modelGroup.subModels?.some(sm => sm.displayName === displayName)) {
            modelGroup.subModels?.push({
              modelNumber: fbModel.modelNumber || fbModel.id,
              displayName,
              storage,
              connectivity: connectivity !== 'Wi-Fi' ? connectivity : undefined
            });
          }
        });
      });
    });
    
    const uniqueModels = Array.from(modelGroups.values());
    
    return uniqueModels;
  }, [
    params.category, 
    params.selectedManufacturer, 
    params.firebaseTabletModels.length // ✅ 얕은 의존성으로 성능 향상
  ]);

  // 🔥 최적화된 Samsung 시리즈 데이터 (성능 개선)
  const firebaseSamsungSeries = useMemo(() => {
    if (!params.firebaseSamsungSmartphones.length) {
      return [];
    }
    
    const seriesMap: Record<string, { name: string; name_ko: string; storages: string[] }[]> = {};
    
    // ✅ 성능 최적화: 단순화된 매핑
    params.firebaseSamsungSmartphones.forEach(device => {
      if (!seriesMap[device.series]) {
        seriesMap[device.series] = [];
      }
      
      const existingModel = seriesMap[device.series].find(m => m.name === device.modelEn);
      if (!existingModel) {
        seriesMap[device.series].push({
          name: device.modelEn,
          name_ko: device.modelKo,
          storages: getActiveStorages(device) // 비활성 용량 제외 = 관리자 그리드와 동일
        });
      }
    });
    
    const result = Object.entries(seriesMap).map(([seriesName, models]) => ({
      name: seriesName,
      models
    }));
    
    return result;
  }, [params.firebaseSamsungSmartphones.length]); // ✅ 얕은 의존성

  // 🎧 웨어러블 모델 변환 (Firebase 기반 - AirPods 등)
  // 🔥 기본 이미지: AirPods 3세대 이미지 사용
  const WEARABLE_DEFAULT_IMAGE = '/assets/airPot/에어팟3세대.png';
  
  const convertedWearableModels = useMemo(() => {
    if (params.category !== '웨어러블' || !params.firebaseWearableModels?.length) {
      return [];
    }
    
    // 🎧 Firebase 웨어러블 모델을 UI용 DeviceModel 형식으로 변환
    const models: DeviceModel[] = params.firebaseWearableModels.map(fbModel => {
      // 🎨 이미지 경로 처리: Firebase에 설정된 이미지가 없으면 기본 AirPods 3세대 이미지 사용
      let modelImage = WEARABLE_DEFAULT_IMAGE;
      if (fbModel.image && fbModel.image !== '/assets/default-wearable.png') {
        modelImage = fbModel.image;
      }
      
      return {
        name: fbModel.modelKo,
        image: modelImage,
        recommended: fbModel.recommended ?? true,
        subcategory: fbModel.series || fbModel.subcategory || 'AirPods',
        // 🎧 웨어러블용 추가 정보 (비활성 구성 제외 = 관리자 그리드와 동일)
        modelNumbers: fbModel.modelNumber ? [fbModel.modelNumber] : [],
        subModels: (fbModel.storageOptions?.length ? getActiveStorages(fbModel) : null)?.map((storage: string) => ({
          modelNumber: fbModel.modelNumber || fbModel.id,
          displayName: `${fbModel.modelKo} ${storage !== '기본' ? storage : ''}`.trim(),
          storage,
          connectivity: '기본'
        })) || [{
          modelNumber: fbModel.modelNumber || fbModel.id,
          displayName: fbModel.modelKo,
          storage: '기본',
          connectivity: '기본'
        }],
        // 🔥 Firebase 원본 ID 보존 (가격 조회용)
        firebaseId: fbModel.id
      };
    });
    
    console.log(`🎧 웨어러블 Firebase 모델 변환: ${models.length}개`);
    
    return models;
  }, [params.category, params.firebaseWearableModels?.length]);

  return {
    firebaseIPhoneSeriesModels,
    firebaseIPhoneStorageByModel,
    convertedTabletModels,
    firebaseSamsungSeries,
    convertedWearableModels // 🎧 웨어러블 추가
  };
};
